/* eslint-disable import/no-extraneous-dependencies */
/**
 * Calendar Storybook Stories
 *
 * These stories render the Calendar feature in isolation without a backend.
 * All context providers (TimeZoneContext, TenantContext, ModalContext, CalendarContext)
 * are mocked with realistic data using helpers in `./helpers/`.
 *
 * StoryCalendarView uses the Calendar QUI molecule (not raw FullCalendar).
 * When calendarOptimizationEnabled=true (default), dayMaxEvents={4} is passed
 * which triggers the custom MonthGrid renderer inside the molecule.
 *
 * Story catalogue:
 *  1. Default        — Empty calendar (no events), MonthGrid mode
 *  2. FewEvents      — 15 visits, 3 time-offs, 2 holidays, MonthGrid mode
 *  3. BusyMonth      — 300 visits, MonthGrid mode, "+N more" popover
 *  4. StressTest     — 1000+ events, MonthGrid mode (target: <2s, DOM <4,000)
 *  5. MixedEvents    — All 4 event types, MonthGrid mode
 *  6. DragDrop       — Interactive drag-and-drop, MonthGrid mode
 *  7. FiltersActive  — Pre-applied filter state, MonthGrid mode
 *  8. FullCalendarMode — Same as BusyMonth but without dayMaxEvents (uses FullCalendar)
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar as CalendarMolecule } from '../../../ui/molecules/Calendar';
import { CalendarContext } from '../CalendarContext';
import { CalendarEventType } from '../constants';
import CalendarEvent from '../nested/CalendarEvent';
import { MockCalendarProviders, buildMockCalendarContextValue } from './helpers/mockProviders';
import { generateMockVisits, generateMockTimeOffs, generateMockHolidays, generateMockGenericEvents, getMonthRange, visitsToProcessedEvents, timeOffsToProcessedEvents, holidaysToProcessedEvents, genericEventsToProcessedEvents } from './helpers/mockData';
// ── Story calendar renderer ────────────────────────────────────────────────
// Reads ProcessedEvent[] from CalendarContext.state.events and renders the
// Calendar QUI molecule. This mirrors the real CalendarComp flow.
//
// When calendarOptimizationEnabled=true (default), dayMaxEvents={4} is passed
// which triggers the custom MonthGrid renderer inside the molecule.
// When calendarOptimizationEnabled=false, FullCalendar is used (no dayMaxEvents).
function toMoleculeEvent(event, isDraggable) {
    var _a, _b, _c, _d, _e, _f, _g;
    let bgColor = '#EFEFF4';
    let borderColor = '#A7A7BB';
    let isAllDay;
    let isEditable = isDraggable;
    let type = CalendarEventType.Generic_event;
    if (event.isVisit) {
        type = CalendarEventType.Visit;
        bgColor = (_c = (_b = (_a = event.assignee) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.color) !== null && _c !== void 0 ? _c : '#4F46E5';
        borderColor = bgColor;
    }
    else if ((_d = event.key) === null || _d === void 0 ? void 0 : _d.includes('TimeOff')) {
        type = CalendarEventType.Timeoff;
        bgColor = (_f = (_e = event.technician) === null || _e === void 0 ? void 0 : _e.color) !== null && _f !== void 0 ? _f : '#ccc';
        borderColor = bgColor;
        isEditable = false;
    }
    else if ((_g = event.key) === null || _g === void 0 ? void 0 : _g.includes('Holiday')) {
        type = CalendarEventType.Holiday;
        bgColor = '#f5f5f888';
        borderColor = bgColor;
        isEditable = false;
        isAllDay = true;
    }
    return {
        id: event.id,
        start: event.start.toDate(),
        end: event.end.toDate(),
        backgroundColor: bgColor,
        borderColor,
        editable: isEditable,
        allDay: isAllDay !== null && isAllDay !== void 0 ? isAllDay : event.allDay,
        // Store feature-specific fields in extendedProps so CalendarEvent can read them
        // via fcProps.event.extendedProps.type / .data — matching what FullCalendar does
        // automatically when these fields are at the root of IFullCalendarEvent.
        extendedProps: {
            type,
            key: event.key,
            data: event,
            highlightingStatus: false
        }
    };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function StoryEventContent(fcProps) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return React.createElement(CalendarEvent, { fcProps: fcProps });
}
function StoryCalendarView({ editable = false, calendarOptimizationEnabled = true }) {
    const { state: { events, view } } = useContext(CalendarContext);
    const cacheRef = useRef([]);
    const [tick, setTick] = useState(0);
    useEffect(() => {
        cacheRef.current = events.map((e) => toMoleculeEvent(e, editable));
        setTick((n) => n + 1);
    }, [events, editable]);
    const memoData = useMemo(() => [...cacheRef.current], [tick]);
    return (React.createElement(CalendarMolecule, { view: view, events: memoData, eventContent: StoryEventContent, dayMaxEvents: calendarOptimizationEnabled ? 4 : undefined, moreLinkClick: calendarOptimizationEnabled ? 'popover' : undefined, showNonCurrentDates: false, eventOrder: "start,title" }));
}
// ── Story wrapper ──────────────────────────────────────────────────────────
function CalendarStory({ events = [], editable, filterState, calendarOptimizationEnabled = true }) {
    const ctxValue = buildMockCalendarContextValue({
        state: {
            events,
            technicians: [
                { label: 'Alex Thompson', value: 1 },
                { label: 'Jordan Lee', value: 2 },
                { label: 'Sam Rivera', value: 3 }
            ],
            selectedTechnicianIds: [],
            selectedSubcontractorIds: [],
            errorMessage: '',
            loading: false,
            detailed: false,
            filters: filterState !== null && filterState !== void 0 ? filterState : {
                values: {
                    building: '',
                    technicianTeam: '',
                    territory: '',
                    tags: [],
                    buildingTags: '',
                    accountTags: '',
                    workOrderTags: '',
                    type: '',
                    inspectionStatus: '',
                    inspectionType: '',
                    visitStatus: '',
                    asset: '',
                    inspectionStatusKeys: [],
                    visitStatusKeys: [],
                    additionalTerritory: ''
                },
                active: {
                    building: false,
                    technicianTeam: false,
                    territory: false,
                    tags: false,
                    buildingTags: false,
                    accountTags: false,
                    workOrderTags: false,
                    type: false,
                    inspectionStatus: false,
                    inspectionType: false,
                    visitStatus: false,
                    asset: false,
                    statuses: false,
                    additionalTerritory: false
                }
            },
            unselectedTechs: {},
            view: 'dayGridMonth',
            statuses: {},
            modalOpen: false
        }
    });
    return (React.createElement(MockCalendarProviders, { calendarContextValue: ctxValue },
        React.createElement(StoryCalendarView, { editable: editable, calendarOptimizationEnabled: calendarOptimizationEnabled })));
}
// ── Shared date range for all stories ─────────────────────────────────────
const NOW = new Date();
const STORY_RANGE = getMonthRange(NOW.getFullYear(), NOW.getMonth());
// ── Meta ──────────────────────────────────────────────────────────────────
const meta = {
    title: 'Features/Calendar',
    component: CalendarStory,
    parameters: {
        layout: 'fullscreen'
    },
    decorators: [
        (Story) => (React.createElement("div", { style: { height: '100vh', padding: '16px', fontFamily: 'HK Grotesk, sans-serif' } },
            React.createElement(Story, null)))
    ]
};
export default meta;
// ── 1. Default — empty calendar ────────────────────────────────────────────
export const Default = {
    name: 'Default (empty)',
    args: {
        events: []
    }
};
// ── 2. Few Events ─────────────────────────────────────────────────────────
const fewVisits = visitsToProcessedEvents(generateMockVisits(15, STORY_RANGE));
const fewTimeOffs = timeOffsToProcessedEvents(generateMockTimeOffs(3, STORY_RANGE));
const fewHolidays = holidaysToProcessedEvents(generateMockHolidays(STORY_RANGE));
export const FewEvents = {
    name: 'Few Events (15 visits + 3 time-offs + holidays)',
    args: {
        events: [...fewVisits, ...fewTimeOffs, ...fewHolidays]
    }
};
// ── 3. Busy Month — tests dayMaxEvents popover ─────────────────────────────
const busyVisits = visitsToProcessedEvents(generateMockVisits(300, STORY_RANGE));
export const BusyMonth = {
    name: 'Busy Month (300 visits — dayMaxEvents popover)',
    args: {
        events: busyVisits
    }
};
// ── 4. Stress Test — 1000+ events, MonthGrid mode ─────────────────────────
const stressVisits = visitsToProcessedEvents(generateMockVisits(800, STORY_RANGE));
const stressTimeOffs = timeOffsToProcessedEvents(generateMockTimeOffs(100, STORY_RANGE));
const stressGeneric = genericEventsToProcessedEvents(generateMockGenericEvents(50, STORY_RANGE));
const stressHolidays = holidaysToProcessedEvents(generateMockHolidays(STORY_RANGE));
export const StressTest = {
    name: 'Stress Test (800 visits + 100 time-offs + 50 generic + holidays) — MonthGrid',
    parameters: {
        docs: {
            description: {
                story: 'Performance scenario with 1000+ events. Uses the custom MonthGrid renderer ' +
                    '(calendarOptimizationEnabled=true → dayMaxEvents=4). ' +
                    'Target: initial paint <2s, DOM nodes <4,000. Only 4 events per cell are ' +
                    'mounted; the rest load on demand when "+N more" is clicked.'
            }
        }
    },
    args: {
        events: [...stressVisits, ...stressTimeOffs, ...stressGeneric, ...stressHolidays],
        calendarOptimizationEnabled: true
    }
};
// ── 5. Mixed Events — all 4 types ─────────────────────────────────────────
const mixedVisits = visitsToProcessedEvents(generateMockVisits(20, STORY_RANGE));
const mixedTimeOffs = timeOffsToProcessedEvents(generateMockTimeOffs(10, STORY_RANGE));
const mixedGeneric = genericEventsToProcessedEvents(generateMockGenericEvents(10, STORY_RANGE));
const mixedHolidays = holidaysToProcessedEvents(generateMockHolidays(STORY_RANGE));
export const MixedEvents = {
    name: 'Mixed Events (all 4 types)',
    args: {
        events: [...mixedVisits, ...mixedTimeOffs, ...mixedGeneric, ...mixedHolidays]
    }
};
// ── 6. Drag/Drop — interactive story ──────────────────────────────────────
const dragVisits = visitsToProcessedEvents(generateMockVisits(20, STORY_RANGE));
export const DragDrop = {
    name: 'Drag/Drop (interactive)',
    args: {
        events: dragVisits,
        editable: true
    }
};
// ── 7. Filters Active ─────────────────────────────────────────────────────
const filteredVisits = visitsToProcessedEvents(generateMockVisits(30, STORY_RANGE));
export const FiltersActive = {
    name: 'Filters Active (visit status filter)',
    args: {
        events: filteredVisits.slice(0, 10),
        filterState: {
            values: {
                building: '',
                technicianTeam: '',
                territory: '',
                tags: [],
                buildingTags: '',
                accountTags: '',
                workOrderTags: '',
                type: '',
                inspectionStatus: '',
                inspectionType: '',
                visitStatus: 'scheduled',
                asset: '',
                inspectionStatusKeys: [],
                visitStatusKeys: ['scheduled']
            },
            active: {
                building: false,
                technicianTeam: false,
                territory: false,
                tags: false,
                buildingTags: false,
                accountTags: false,
                workOrderTags: false,
                type: false,
                inspectionStatus: false,
                inspectionType: false,
                visitStatus: true,
                asset: false,
                statuses: true
            }
        }
    }
};
// ── 8. FullCalendar mode — no optimization ─────────────────────────────────
// Reference: same 300-visit dataset rendered through FullCalendar (no dayMaxEvents).
// Use this to compare against MonthGrid stories for visual parity.
const fullCalendarVisits = visitsToProcessedEvents(generateMockVisits(300, STORY_RANGE));
export const FullCalendarMode = {
    name: 'FullCalendar Mode (optimization OFF — reference)',
    parameters: {
        docs: {
            description: {
                story: 'calendarOptimizationEnabled=false → dayMaxEvents is not passed → ' +
                    'FullCalendar renders the month view directly. ' +
                    'Use this as a visual reference alongside the MonthGrid stories.'
            }
        }
    },
    args: {
        events: fullCalendarVisits,
        calendarOptimizationEnabled: false
    }
};
